// =============================================================
// קובץ: C:\gocode\projects\cartax\git\cartax_fe\src\components\shared\EPAVehicleSelector.tsx
// =============================================================

import React, { useState, useEffect, useMemo } from "react";
import { epaService } from "../../services/enduser/epaService";
import { MotionButton } from "./MotionCard";

interface EPAVehicleSelectorProps {
  onSelectVehicle: (data: {
    co2_city: number;
    co_city: number;
    hc_city: number;
    nox_city: number;
    pm_city: number;
    co2_highway: number;
    co_highway: number;
    hc_highway: number;
    nox_highway: number;
    pm_highway: number;
  }) => void;
}

const EPAVehicleSelector: React.FC<EPAVehicleSelectorProps> = ({
  onSelectVehicle,
}) => {
  // State for selections
  const [selectedYear, setSelectedYear] = useState<number | "">("");
  const [selectedMake, setSelectedMake] = useState<string>("");
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [selectedFuel, setSelectedFuel] = useState<string>("");

  // State for dropdown options
  const [years, setYears] = useState<number[]>([]);
  const [makes, setMakes] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [fuels, setFuels] = useState<string[]>([]);

  // State for search in model dropdown
  const [modelSearch, setModelSearch] = useState<string>("");
  const [isModelDropdownOpen, setIsModelDropdownOpen] =
    useState<boolean>(false);

  // Loading states
  const [loadingYears, setLoadingYears] = useState<boolean>(false);
  const [loadingMakes, setLoadingMakes] = useState<boolean>(false);
  const [loadingModels, setLoadingModels] = useState<boolean>(false);
  const [loadingFuels, setLoadingFuels] = useState<boolean>(false);
  const [loadingVehicle, setLoadingVehicle] = useState<boolean>(false);

  // Error state
  const [error, setError] = useState<string>("");

  // Load years on mount
  useEffect(() => {
    const fetchYears = async () => {
      setLoadingYears(true);
      setError("");
      try {
        const yearsData = await epaService.getYears();
        setYears(yearsData.sort((a, b) => b - a)); // Sort descending (newest first)
      } catch (err) {
        setError("שגיאה בטעינת שנות דגם");
        console.error("Error fetching years:", err);
      } finally {
        setLoadingYears(false);
      }
    };
    fetchYears();
  }, []);

  // Load makes when year changes
  useEffect(() => {
    if (!selectedYear) {
      setMakes([]);
      setSelectedMake("");
      return;
    }

    const fetchMakes = async () => {
      setLoadingMakes(true);
      setError("");
      setMakes([]);
      setModels([]);
      setFuels([]);
      setSelectedMake("");
      setSelectedModel("");
      setSelectedFuel("");

      try {
        const makesData = await epaService.getMakes(selectedYear as number);
        setMakes(makesData.sort());
      } catch (err) {
        setError("שגיאה בטעינת יצרנים");
        console.error("Error fetching makes:", err);
      } finally {
        setLoadingMakes(false);
      }
    };
    fetchMakes();
  }, [selectedYear]);

  // Load models when make changes
  useEffect(() => {
    if (!selectedYear || !selectedMake) {
      setModels([]);
      setSelectedModel("");
      return;
    }

    const fetchModels = async () => {
      setLoadingModels(true);
      setError("");
      setModels([]);
      setFuels([]);
      setSelectedModel("");
      setSelectedFuel("");
      setModelSearch("");

      try {
        const modelsData = await epaService.getModels(
          selectedYear as number,
          selectedMake
        );
        setModels(modelsData.sort());
      } catch (err) {
        setError("שגיאה בטעינת דגמים");
        console.error("Error fetching models:", err);
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModels();
  }, [selectedYear, selectedMake]);

  // Load fuels when model changes
  useEffect(() => {
    if (!selectedYear || !selectedMake || !selectedModel) {
      setFuels([]);
      setSelectedFuel("");
      return;
    }

    const fetchFuels = async () => {
      setLoadingFuels(true);
      setError("");
      setFuels([]);
      setSelectedFuel("");

      try {
        const fuelsData = await epaService.getFuels(
          selectedYear as number,
          selectedMake,
          selectedModel
        );
        setFuels(fuelsData);

        // Auto-select if only one fuel type
        if (fuelsData.length === 1) {
          setSelectedFuel(fuelsData[0]);
        }
      } catch (err) {
        setError("שגיאה בטעינת סוגי דלק");
        console.error("Error fetching fuels:", err);
      } finally {
        setLoadingFuels(false);
      }
    };
    fetchFuels();
  }, [selectedYear, selectedMake, selectedModel]);

  // Filtered models based on search
  const filteredModels = useMemo(() => {
    if (!modelSearch.trim()) return models;
    const search = modelSearch.toLowerCase();
    return models.filter((model) => model.toLowerCase().includes(search));
  }, [models, modelSearch]);

  // Handle vehicle selection
  const handleUseData = async () => {
    if (!selectedYear || !selectedMake || !selectedModel || !selectedFuel) {
      setError("יש לבחור את כל השדות");
      return;
    }

    setLoadingVehicle(true);
    setError("");

    try {
      const vehicleData = await epaService.getVehicleData(
        selectedYear as number,
        selectedMake,
        selectedModel,
        selectedFuel
      );

      if (!vehicleData) {
        setError("לא נמצאו נתונים לרכב זה");
        return;
      }

      // Map EPA data to modal inputs
      onSelectVehicle({
        co2_city: vehicleData.ftp_co2 || 0,
        co_city: vehicleData.ftp_co || 0,
        hc_city: vehicleData.ftp_thc || 0,
        nox_city: vehicleData.ftp_nox || 0,
        pm_city: vehicleData.ftp_pm || 0,
        co2_highway: vehicleData.hwy_co2 || 0,
        co_highway: vehicleData.hwy_co || 0,
        hc_highway: vehicleData.hwy_thc || 0,
        nox_highway: vehicleData.hwy_nox || 0,
        pm_highway: vehicleData.hwy_pm || 0,
      });
    } catch (err) {
      setError("שגיאה בטעינת נתוני הרכב");
      console.error("Error fetching vehicle data:", err);
    } finally {
      setLoadingVehicle(false);
    }
  };

  // Check if all fields are selected
  const isComplete =
    selectedYear && selectedMake && selectedModel && selectedFuel;

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xl">🇺🇸</span>
        <h4 className="font-semibold text-yellow-800">בחירת רכב ממאגר EPA</h4>
      </div>

      <p className="text-sm text-yellow-700 mb-4">
        בחר רכב מהרשימה כדי למלא את נתוני הזיהום אוטומטית
      </p>

      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 px-3 py-2 rounded mb-3 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {/* Year Dropdown */}
        <div>
          <label
            htmlFor="epa-year"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            שנה
          </label>
          <select
            id="epa-year"
            value={selectedYear}
            onChange={(e) =>
              setSelectedYear(e.target.value ? parseInt(e.target.value) : "")
            }
            disabled={loadingYears}
            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
          >
            <option value="">{loadingYears ? "טוען..." : "בחר שנה"}</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Make Dropdown */}
        <div>
          <label
            htmlFor="epa-make"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            יצרן
          </label>
          <select
            id="epa-make"
            value={selectedMake}
            onChange={(e) => setSelectedMake(e.target.value)}
            disabled={!selectedYear || loadingMakes}
            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm disabled:bg-gray-100"
          >
            <option value="">{loadingMakes ? "טוען..." : "בחר יצרן"}</option>
            {makes.map((make) => (
              <option key={make} value={make}>
                {make}
              </option>
            ))}
          </select>
        </div>

        {/* Model Dropdown with Search */}
        <div className="relative">
          <label
            htmlFor="epa-model-search"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            דגם
          </label>
          <div className="relative">
            <input
              id="epa-model-search"
              type="text"
              value={selectedModel || modelSearch}
              onChange={(e) => {
                setModelSearch(e.target.value);
                setSelectedModel("");
                setIsModelDropdownOpen(true);
              }}
              onFocus={() => setIsModelDropdownOpen(true)}
              placeholder={loadingModels ? "טוען..." : "הקלד לחיפוש..."}
              disabled={!selectedMake || loadingModels}
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm disabled:bg-gray-100"
            />
            {isModelDropdownOpen &&
              filteredModels.length > 0 &&
              !selectedModel && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredModels.map((model) => (
                    <button
                      key={model}
                      type="button"
                      onClick={() => {
                        setSelectedModel(model);
                        setModelSearch("");
                        setIsModelDropdownOpen(false);
                      }}
                      className="w-full text-right px-3 py-2 hover:bg-yellow-100 text-sm border-b border-gray-100 last:border-b-0"
                    >
                      {model}
                    </button>
                  ))}
                </div>
              )}
          </div>
          {selectedModel && (
            <button
              type="button"
              onClick={() => {
                setSelectedModel("");
                setModelSearch("");
              }}
              className="absolute left-2 top-8 text-gray-400 hover:text-gray-600"
              title="נקה בחירה"
            >
              ×
            </button>
          )}
        </div>

        {/* Fuel Dropdown */}
        <div>
          <label
            htmlFor="epa-fuel"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            סוג דלק
          </label>
          <select
            id="epa-fuel"
            value={selectedFuel}
            onChange={(e) => setSelectedFuel(e.target.value)}
            disabled={!selectedModel || loadingFuels}
            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm disabled:bg-gray-100"
          >
            <option value="">{loadingFuels ? "טוען..." : "בחר דלק"}</option>
            {fuels.map((fuel) => (
              <option key={fuel} value={fuel}>
                {fuel}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Use Data Button */}
      <MotionButton
        onClick={handleUseData}
        disabled={!isComplete || loadingVehicle}
        className={`w-full py-2 px-4 rounded-lg transition-colors text-sm font-medium ${
          isComplete && !loadingVehicle
            ? "bg-yellow-500 hover:bg-yellow-600 text-white"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        {loadingVehicle ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            טוען נתונים...
          </span>
        ) : (
          "📥 השתמש בנתונים אלו"
        )}
      </MotionButton>

      {/* Selected vehicle info */}
      {isComplete && (
        <div className="mt-2 text-xs text-yellow-700 text-center">
          נבחר: {selectedYear} {selectedMake} {selectedModel} ({selectedFuel})
        </div>
      )}
    </div>
  );
};

export default EPAVehicleSelector;
